import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, ArrowRight, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';

const InvoiceSetup = () => {
  const { api } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', business_name: '', nif: '', address: '', city: '',
    postal_code: '', phone: '', email: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.address || !form.city || !form.phone || !form.email) return;
    setSaving(true);
    try {
      await api.post('/shops', {
        ...form,
        description: 'Invoice Portal Business',
      });
      navigate('/invoices/dashboard');
    } catch (e) {
      alert(e.response?.data?.detail || 'Error creating business');
    } finally {
      setSaving(false);
    }
  };

  const update = (field, val) => setForm(f => ({ ...f, [field]: val }));

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4" data-testid="invoice-setup-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg bg-white border-2 border-slate-100 p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-[#1e3a5f]/10 rounded-sm flex items-center justify-center">
            <Building2 className="w-6 h-6 text-[#1e3a5f]" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-[#1e3a5f]">{t('inv_setup_title') || 'Set Up Your Business'}</h2>
            <p className="text-slate-500 text-sm">{t('inv_setup_desc') || 'Enter your business details for VeriFactu invoices'}</p>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-[#1e3a5f] mb-1">{t('inv_setup_name') || 'Business Name'} *</label>
              <input type="text" required value={form.name} onChange={e => update('name', e.target.value)}
                className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none"
                data-testid="setup-biz-name" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1e3a5f] mb-1">{t('inv_setup_legal') || 'Legal Name'}</label>
              <input type="text" value={form.business_name} onChange={e => update('business_name', e.target.value)}
                className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1e3a5f] mb-1">NIF *</label>
              <input type="text" value={form.nif} onChange={e => update('nif', e.target.value)}
                placeholder="B12345678"
                className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none"
                data-testid="setup-nif" />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-[#1e3a5f] mb-1">{t('inv_customer_address') || 'Address'} *</label>
              <input type="text" required value={form.address} onChange={e => update('address', e.target.value)}
                className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1e3a5f] mb-1">{t('inv_customer_city') || 'City'} *</label>
              <input type="text" required value={form.city} onChange={e => update('city', e.target.value)}
                className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1e3a5f] mb-1">{t('inv_customer_postal') || 'Postal Code'}</label>
              <input type="text" value={form.postal_code} onChange={e => update('postal_code', e.target.value)}
                className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1e3a5f] mb-1">{t('inv_setup_phone') || 'Phone'} *</label>
              <input type="tel" required value={form.phone} onChange={e => update('phone', e.target.value)}
                className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1e3a5f] mb-1">{t('inv_customer_email') || 'Email'} *</label>
              <input type="email" required value={form.email} onChange={e => update('email', e.target.value)}
                className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none" />
            </div>
          </div>
          <button type="submit" disabled={saving}
            className="vf-btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 mt-6"
            data-testid="setup-submit-btn">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowRight className="w-5 h-5" />}
            {t('inv_setup_submit') || 'Create Business & Start Invoicing'}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

export default InvoiceSetup;
